import { Alert, Box, IconButton, Skeleton, Tooltip } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { SECURITY_ACTIONS, useSecurity } from '../../../auth/Security';
import I18n, { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import Notes from '../../../notes/Notes';
import type { NotesProps } from '../../../notes/types';
import type { SidePanelSubject } from '../../types';
import { PanelNoAccess } from '../../ui/components/PanelNoAccess';
import { usePanelCommands } from '../../ui/hooks/usePanelCommands';
import { usePanelSession } from '../../ui/hooks/usePanelSession';
import type { NotesPanelOptions, NotesPanelPayload } from './types';
import { useNotesPanelStatusNote } from './useNotesPanelStatusNote';

const pendingSx = { p: 2 };

export function NotesSidePanel(options: NotesPanelOptions) {
    const { sessionId, subject, payload } = usePanelSession<NotesPanelPayload>();
    const { close } = usePanelCommands();
    const t = useI18n();
    const decision = useSecurity(SECURITY_ACTIONS.COMMENT, {
        entityName: subject?.entityName,
        entitySecurity: subject?.security
    });
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    const closeLabel = t('dialog.close');
    const closeAffordance = (
        <Tooltip title={closeLabel}>
            <IconButton aria-label={closeLabel} data-testid="notes-panel-close" onClick={handleClose}>
                <Icons.Close />
            </IconButton>
        </Tooltip>
    );

    if (decision.pending) {
        return (
            <Box sx={pendingSx} role="status" aria-busy="true" data-testid="notes-panel-pending">
                {closeAffordance}
                <Skeleton height={48} />
                <Skeleton variant="rectangular" height={160} />
            </Box>
        );
    }
    if (!subject?.reference.id || !decision.allowed || (payload?.intent === 'singleNote' && !payload.noteId)) {
        return <PanelNoAccess hint={closeAffordance} />;
    }

    return (
        <AuthorizedNotesSidePanel
            key={`${sessionId}:${subject.reference.id}`}
            subject={subject}
            payload={payload}
            options={options}
        />
    );
}

function AuthorizedNotesSidePanel({
    subject,
    payload,
    options
}: {
    subject: SidePanelSubject;
    payload?: NotesPanelPayload;
    options: NotesPanelOptions;
}) {
    const status = options.selectStatusNote?.(subject.record);
    const isStatusNoteAction = payload?.intent === 'statusNote';
    const { callbacks, handleClose, errorToken } = useNotesPanelStatusNote(status, isStatusNoteAction);
    const { close } = usePanelCommands();
    const t = useI18n();
    const handleDismiss = useCallback(() => {
        close();
    }, [close]);
    const closeLabel = t('dialog.close');
    const errorCloseAction = useMemo(
        () => (
            <Tooltip title={closeLabel}>
                <IconButton aria-label={closeLabel} onClick={handleDismiss} data-testid="notes-panel-error-close">
                    <Icons.Close />
                </IconButton>
            </Tooltip>
        ),
        [closeLabel, handleDismiss]
    );
    const singleNoteView = useMemo<NotesProps['singleNoteView']>(() => {
        if (payload?.intent !== 'singleNote' && !(isStatusNoteAction && payload?.noteId)) return undefined;
        const liveNote = status?.statusNote;
        const liveNoteId = liveNote?.reference?.id ?? liveNote?.id;
        const view: NotesProps['singleNoteView'] = {
            noteId: payload?.noteId,
            noteData: liveNoteId === payload?.noteId ? (liveNote ?? undefined) : undefined,
            disableEditing: options.disableEditing
        };
        return view;
    }, [isStatusNoteAction, options.disableEditing, payload?.intent, payload?.noteId, status?.statusNote]);
    const title = useMemo(
        () => <I18n token="notes.panel.title" values={{ entityName: subject.label ?? subject.reference.label }} />,
        [subject.label, subject.reference.label]
    );

    return (
        <>
            {errorToken && (
                <Alert severity="error" action={errorCloseAction}>
                    <I18n token={errorToken} />
                </Alert>
            )}
            <Notes
                id="notes"
                entity={subject.entityName}
                entityReference={subject.reference}
                title={title}
                onClose={handleClose}
                singleNoteView={singleNoteView}
                highlightedNoteId={payload?.noteId}
                focusTrigger={payload?.focusRequest}
                onAssociateStatusNote={callbacks}
                disableTranslation={options.disableTranslation}
            />
        </>
    );
}
